// Google Local Services Ads — billing reconciliation and lead-quality frame.
//
// The client-facing story starts with leads Google did not charge for, then
// adds spend, total-lead CPL, call/message activity, the cadence actually returned
// by AgencyAnalytics, and any available lead breakdowns. Every helper is local
// to this frame; missing fields and missing breakdown pulls render nothing.

import { SectionPanel } from "@/components/dashboard/SectionPanel";
import { TrendChart } from "@/components/dashboard/TrendChart";
import type { LsaBreakdownRow, LsaSection, LsaTrendRow } from "@/types/clientAnalytics";

function formatNumber(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDuration(totalSeconds: number): string {
  const rounded = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function SourceChip() {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-muted px-2.5 py-0.5 text-dash-context font-semibold text-muted-foreground">
      <span className="h-1.5 w-1.5 rounded-sm bg-coral" />
      Google LSA
    </span>
  );
}

function MetricTile({ label, value, context }: { label: string; value: string; context?: string }) {
  return (
    <div className="min-w-[150px] flex-1 p-4">
      <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-dash-value font-bold tabular text-foreground">{value}</div>
      {context && <div className="mt-1 text-dash-context text-muted-foreground">{context}</div>}
    </div>
  );
}

function isPositive(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function hasMeasuredActivity(data: LsaSection): boolean {
  if (
    isPositive(data.total_leads) ||
    isPositive(data.cost) ||
    isPositive(data.phone_calls) ||
    isPositive(data.messages) ||
    isPositive(data.duration)
  ) {
    return true;
  }
  const breakdownHasActivity = [
    ...data.by_charge_status,
    ...data.by_lead_type,
    ...data.by_lead_category,
  ].some((row) => isPositive(row.leads));
  if (breakdownHasActivity) return true;
  return data.over_time.some(
    (row) =>
      isPositive(row.leads) ||
      isPositive(row.cost) ||
      isPositive(row.phone_calls) ||
      isPositive(row.messages) ||
      isPositive(row.duration),
  );
}

function findBreakdownValue(rows: LsaBreakdownRow[], label: string): number | null {
  const match = rows.find((row) => row.label.trim().toLowerCase() === label.toLowerCase());
  return match && Number.isFinite(match.leads) ? match.leads : null;
}

function buildChargeRows(data: LsaSection): LsaBreakdownRow[] {
  const measuredRows = data.by_charge_status.filter((row) => isPositive(row.leads));
  if (measuredRows.length > 0) return measuredRows;

  const fallback: LsaBreakdownRow[] = [];
  if (isPositive(data.charged)) fallback.push({ label: "Charged", leads: data.charged });
  if (isPositive(data.not_charged)) {
    fallback.push({ label: "Not Charged", leads: data.not_charged });
  }
  return fallback;
}

function chargeRowClass(label: string): string {
  const normalized = label.trim().toLowerCase();
  if (normalized === "not charged") return "bg-pace-good-light text-foreground";
  if (normalized === "charged") return "bg-muted text-foreground";
  return "bg-coral-light text-coral-dark";
}

function ChargeBreakdown({ rows }: { rows: LsaBreakdownRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-border bg-card p-4"
      data-testid="lsa-billing-outcomes"
    >
      <div>
        <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          Billing outcomes
        </div>
        <div className="mt-0.5 text-dash-context text-muted-foreground">
          Billing statuses returned by Google for this period.
        </div>
      </div>

      {/* Independent status tiles, not a 100% stacked bar. AA can return
          additional outcomes (Credited today, other labels in the future),
          and the returned rows are not guaranteed to be an exhaustive
          partition. Rendering each measured count directly avoids inventing
          shares from either their sum or total_leads. */}
      <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3" role="list">
        {rows.map((row) => (
          <div
            key={row.label}
            className={`flex items-center justify-between gap-3 rounded-md px-3 py-2 ${chargeRowClass(row.label)}`}
            role="listitem"
          >
            <span className="text-dash-context font-medium">{row.label}</span>
            <span className="text-dash-subtitle font-bold tabular">{formatNumber(row.leads)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function trendTitle(rows: LsaTrendRow[]): string {
  if (rows.length > 0 && rows.every((row) => /^\d{4}-\d{2}$/.test(row.date))) {
    return "Monthly leads";
  }
  if (rows.length > 0 && rows.every((row) => /^\d{4}-\d{2}-\d{2}$/.test(row.date))) {
    return "Daily leads";
  }
  return "Leads over time";
}

function formatTrendDate(value: string): string {
  const month = /^(\d{4})-(\d{2})$/.exec(value);
  if (month) {
    const date = new Date(Date.UTC(Number(month[1]), Number(month[2]) - 1, 1));
    return new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(date);
  }
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (day) return `${Number(day[2])}/${Number(day[3])}`;
  return value;
}

function BreakdownCard({ title, rows }: { title: string; rows: LsaBreakdownRow[] }) {
  const visible = rows.filter((row) => isPositive(row.leads));
  if (visible.length === 0) return null;
  const max = Math.max(...visible.map((row) => row.leads));

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="mt-3 space-y-3">
        {visible.map((row) => (
          <div key={row.label}>
            <div className="mb-1 flex items-center justify-between gap-3 text-dash-context">
              <span className="text-foreground">{row.label}</span>
              <span className="shrink-0 font-semibold tabular text-muted-foreground">
                {formatNumber(row.leads)}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-coral" style={{ width: `${(row.leads / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LsaPanel({ data }: { data?: LsaSection | null }) {
  if (!data) return null;

  if (!hasMeasuredActivity(data)) {
    return (
      <SectionPanel
        title="Local Services Ads"
        caption="Google-screened leads · billing & lead quality"
        action={<SourceChip />}
      >
        <div className="p-5">
          <div className="rounded-lg border border-border bg-muted px-4 py-4 text-dash-context text-foreground">
            Local Services Ads is connected, but no lead activity was reported for this period.
          </div>
        </div>
      </SectionPanel>
    );
  }

  const currency = data.currency ?? "USD";
  const notCharged =
    data.not_charged ?? findBreakdownValue(data.by_charge_status, "Not Charged");
  const chargeRows = buildChargeRows(data);
  const trendRows = data.over_time.filter(
    (row) => row.date && Number.isFinite(row.leads),
  );
  const showTrend = trendRows.length >= 2 && trendRows.some((row) => row.leads > 0);
  const showActivityMetrics =
    isPositive(data.phone_calls) || isPositive(data.messages) || isPositive(data.duration);
  const showBreakdowns =
    data.by_lead_type.some((row) => isPositive(row.leads)) ||
    data.by_lead_category.some((row) => isPositive(row.leads));

  return (
    <SectionPanel
      title="Local Services Ads"
      caption="Google-screened leads · billing & lead quality"
      action={<SourceChip />}
    >
      <div className="flex flex-col gap-4 p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]">
          {notCharged !== null && (
            <div className="rounded-lg border border-pace-good bg-pace-good-light p-5 text-foreground">
              <div className="text-dash-label font-semibold uppercase tracking-wide">Not charged</div>
              <h4
                className="mt-2 flex flex-wrap items-baseline gap-2 font-display text-[44px] font-bold leading-none tabular"
                aria-label={`${formatNumber(notCharged)} leads not charged`}
              >
                {formatNumber(notCharged)}
                <span className="font-sans text-dash-subtitle font-semibold">leads</span>
              </h4>
              <div className="mt-3 text-dash-context">Google did not bill these leads.</div>
            </div>
          )}
          <ChargeBreakdown rows={chargeRows} />
        </div>

        <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border bg-card">
          {isPositive(data.total_leads) && (
            <MetricTile label="Total leads" value={formatNumber(data.total_leads)} />
          )}
          {isPositive(data.cost) && (
            <MetricTile label="Cost" value={formatMoney(data.cost, currency)} context="LSA spend" />
          )}
          {isPositive(data.cost_per_lead) && (
            <MetricTile
              label="Cost per lead"
              value={formatMoney(data.cost_per_lead, currency)}
              context="across all reported leads"
            />
          )}
        </div>

        {showTrend && (
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              {trendTitle(trendRows)}
            </div>
            <div className="mt-3">
              <TrendChart
                data={trendRows}
                series={[{ dataKey: "leads", name: "Leads" }]}
                xKey="date"
                xTickFormatter={formatTrendDate}
                height={170}
                showCurrentValue={false}
              />
            </div>
          </div>
        )}

        {showActivityMetrics && (
          <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border bg-card">
            {isPositive(data.phone_calls) && (
              <MetricTile label="Phone calls" value={formatNumber(data.phone_calls)} />
            )}
            {isPositive(data.messages) && (
              <MetricTile label="Messages" value={formatNumber(data.messages)} />
            )}
            {isPositive(data.duration) && (
              <MetricTile
                label="Call duration"
                value={formatDuration(data.duration)}
                context="total connected time"
              />
            )}
          </div>
        )}

        {showBreakdowns && (
          <div className="grid gap-4 lg:grid-cols-2">
            <BreakdownCard title="Lead type" rows={data.by_lead_type} />
            <BreakdownCard title="Service category" rows={data.by_lead_category} />
          </div>
        )}
      </div>
    </SectionPanel>
  );
}
